// src/lib/supabase/queries/comment-queries.ts
/**
 * .tuMetrics {"tu1": 0.88, "tu2": 0.89, "tu3": 6, "notes": "Comment row mappers + CRUD queries; TOAST-safe content mapping (no String(undefined))", "tuVersion": "2.5"}
 * .help "Maps comment rows between the database and the app, and provides comment fetch/insert/update/delete queries."
 */
import { supabase } from '../../supabase';
import { Comment } from '../../../types';

// July 2026: types/supabase.ts (generated Database types) is an empty file, so
// the old `Database['public']['Tables']['comments']` lookups never type-
// checked. Hand-written row shapes until codegen is wired up.
type Json = string | number | boolean | null | { [key: string]: Json | undefined } | Json[];

interface CommentRow {
  id: string;
  user_id: string;
  entity_type: string;
  entity_id: string;
  content: string | null;
  created_at: string;
  updated_at: string;
  parent_comment_id: string | null;
  mentions: string[] | null;
  reactions: Json | null;
  company_id: string | null;
  stream_type: string | null;
  recipient_stream_id: string | null;
  shared_item_id: string | null;
  shared_item_details: Json | null;
  tags: string[] | null;
  highlighted_text: string | null;
  highlight_position: number | null;
  highlight_end_position: number | null;
}

type CommentInsert = Partial<Omit<CommentRow, 'id' | 'created_at' | 'updated_at' | 'reactions' | 'shared_item_details'>> & {
  user_id: string;
  entity_type: string;
  entity_id: string;
  content: string;
  reactions?: Record<string, string[]> | string | null;
  shared_item_details?: Json | string | null;
};

type CommentUpdate = Partial<CommentInsert>;

// Helper function to safely parse JSONB data
const safeParseJsonb = <T>(jsonbData: string | Json | null): T | undefined => {
  if (jsonbData === null || jsonbData === undefined) {
    return undefined;
  }
  try {
    // If it's a string, parse it. Otherwise, assume it's already an object/array.
    return (typeof jsonbData === 'string' ? JSON.parse(jsonbData) : jsonbData) as T;
  } catch (e) {
    console.error("Error parsing JSONB data:", e, "Data:", jsonbData);
    return undefined;
  }
};

export const mapCommentFromDB = (row: CommentRow): Comment => ({
  id: row.id,
  userId: row.user_id,
  entityType: row.entity_type as Comment['entityType'],
  entityId: row.entity_id,
  // July 2026: never String(undefined) → the literal text "undefined".
  // Realtime UPDATE payloads omit unchanged TOASTed columns (content > ~2KB),
  // so row.content can legitimately be absent — map to '' (mergeSafe keeps
  // the existing text on merge).
  content: row.content == null ? '' : String(row.content),
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  parentCommentId: row.parent_comment_id || undefined,
  mentions: row.mentions || [],
  reactions: safeParseJsonb<Record<string, string[]>>(row.reactions) || {},
  companyId: row.company_id || undefined,
  streamType: row.stream_type as Comment['streamType'] || undefined,
  recipientStreamId: row.recipient_stream_id || undefined,
  sharedItemId: row.shared_item_id || undefined,
  sharedItemDetails: safeParseJsonb<Comment['sharedItemDetails']>(row.shared_item_details),
  tags: row.tags || [], // Include the new tags column
  // Highlight-to-comment fields (December 1, 2025)
  highlightedText: row.highlighted_text || undefined,
  highlightPosition: row.highlight_position || undefined,
  highlightEndPosition: row.highlight_end_position || undefined,
});

export const mapCommentToDB = (comment: Omit<Comment, 'id' | 'createdAt' | 'updatedAt'>): CommentInsert => ({
  user_id: comment.userId,
  entity_type: comment.entityType,
  entity_id: comment.entityId,
  content: comment.content,
  parent_comment_id: comment.parentCommentId || null,
  mentions: comment.mentions,
  reactions: comment.reactions,
  company_id: comment.companyId || null,
  stream_type: comment.streamType || null,
  recipient_stream_id: comment.recipientStreamId || null,
  shared_item_id: comment.sharedItemId || null,
  shared_item_details: comment.sharedItemDetails ? JSON.stringify(comment.sharedItemDetails) : null,
  tags: comment.tags || [], // Include the new tags column
  // Highlight-to-comment fields (December 1, 2025)
  highlighted_text: comment.highlightedText || null,
  highlight_position: comment.highlightPosition || null,
  highlight_end_position: comment.highlightEndPosition || null,
});

export const mapCommentUpdatesToDB = (updates: Partial<Comment>): CommentUpdate => {
  const dbUpdates: CommentUpdate = {};
  if (updates.content !== undefined) dbUpdates.content = updates.content;
  if (updates.reactions !== undefined) dbUpdates.reactions = updates.reactions;
  if (updates.tags !== undefined) dbUpdates.tags = updates.tags; // Include the new tags column
  return dbUpdates;
};

// --- Supabase Interaction Functions ---

export const fetchComments = async (companyId: string) => {
  const { data, error } = await supabase
    .from('comments')
    .select('*')
    .eq('company_id', companyId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data.map(mapCommentFromDB);
};

export const insertComment = async (commentData: CommentInsert) => {
  const { data, error } = await supabase
    .from('comments')
    .insert(commentData)
    .select()
    .single();

  if (error) throw error;
  return mapCommentFromDB(data);
};

export const updateCommentById = async (commentId: string, updates: CommentUpdate) => {
  const { data, error } = await supabase
    .from('comments')
    .update(updates)
    .eq('id', commentId)
    .select()
    .single();

  if (error) throw error;
  return mapCommentFromDB(data);
};

export const deleteCommentById = async (commentId: string) => {
  const { error } = await supabase
    .from('comments')
    .delete()
    .eq('id', commentId);

  if (error) throw error;
  return true;
};
